import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiLink from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Container from '@mui/material/Container'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Popover from '@mui/material/Popover'
import Rating from '@mui/material/Rating'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SettingsIcon from '@mui/icons-material/Settings'

import { Inventory } from '../types/inventory'
import { weightValuesToDisplayValues } from '../utils/inventoryUtils'
import { useContextHook } from '../store/context'
import { get, setPageTitle } from '../utils/service'
import { PagedResponse } from '../types/pagedResponse'
import ListHeader from '../components/listheader/listheader'
import Alert from '../components/alert/alert'
import TooltipIconButton from '../components/fields/tooltipIconButton'
import Pagination from '../components/fields/pagination'
import SortableTableHead from '../components/fields/sortableTableHead'
import { SortType } from '../types/enums/sortType'
import { getBigWeightSuffix, getSingleBigWeightSuffix } from '../utils/weightUtils'
import { toDateString } from '../utils/dateUtils'
import { isOsxSafari } from '../utils/service'
import ResponsiveTableHeader from '../components/fields/responsiveTableHeader'
import { getInventoryListHiddenColumns, setInventoryListHiddenColumns } from '../utils/localStorageUtils'

const CULTIVAR_COLUMN = 'cultivar'
const FARM_COLUMN = 'farm'
const REGION_COLUMN = 'region'
const COUNTRY_COLUMN = 'country'
const EXPORTER_COLUMN = 'exporter'
const IMPORTER_COLUMN = 'importer'
const PRODUCER_COLUMN = 'producer'
const ELEVATION_COLUMN = 'elevation'
const MOISTURE_COLUMN = 'moisture'
const WATER_ACTIVITY_COLUMN = 'water_activity'
const DENSITY_COLUMN = 'density'
const QUALITY_COLUMN = 'quality'
const PRICE_COLUMN = 'price'
const REG_DATE_COLUMN = 'reg_date'
const CURRENT_WEIGHT_COLUMN = 'current_weight'
const INITIAL_WEIGHT_COLUMN = 'initial_weight'

enum InventoryFilter {
  All = 1,
  Current = 2,
  Historic = 3,
}

const getFilterFragment = (filter: InventoryFilter): string => {
  if (filter === InventoryFilter.Current || filter === InventoryFilter.Historic) {
    return `&is_archived=${filter === InventoryFilter.Historic}`
  }
  return ''
}

const getInventoryUrl = (customerId: number, filter: InventoryFilter, search: string, sortFragment: string): string => {
  const sortFragmentString = sortFragment ? `&ordering=${sortFragment}` : ''
  const filterFragment = getFilterFragment(filter)
  const searchFragment = search ? `&search=${search}` : ''
  return `inventories/?customer=${customerId}${filterFragment}${searchFragment}${sortFragmentString}`
}

const InventoryView = (): JSX.Element => {
  const { state } = useContextHook()
  const { t } = useTranslation()
  const theme = useTheme()

  const [selectedFilter, setSelectedFilter] = React.useState<InventoryFilter>(InventoryFilter.All)
  const [searchString, setSearchString] = React.useState<string>('')
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [hiddenColumns, setHiddenColumns] = React.useState<Array<string>>(getInventoryListHiddenColumns())

  const [isLoadingInventory, setIsLoadingInventory] = React.useState<boolean>(false)
  const [loadInventoryUrl, setLoadInventoryUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Inventory> | null>(null)
  const [inventoryInfo, setInventoryInfo] = React.useState<Array<Inventory> | null>(null)
  const [hasInventoryError, setHasInventoryError] = React.useState<boolean>(false)
  const [sortFragment, setSortFragment] = React.useState<string>('')

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const closeFilterMenu = (): void => {
    setFilterMenuAnchorEl(null)
  }

  const onSelectFilter = (newFilter: InventoryFilter): void => {
    if (newFilter !== selectedFilter) {
      setSelectedFilter(newFilter)
      closeFilterMenu()
    }
  }

  React.useEffect(() => {
    // update localStorage when hidden columns array is changed
    setInventoryListHiddenColumns(hiddenColumns)
  }, [hiddenColumns])

  // update page title
  React.useEffect(() => {
    setPageTitle(t('inventory.inventory'))
  }, [t])

  React.useEffect(() => {
    if (state.currentCustomer?.id) {
      setPaginationSettings(null)
      setLoadInventoryUrl(getInventoryUrl(state.currentCustomer.id, selectedFilter, searchString, sortFragment))
    }
  }, [state.currentCustomer?.id, selectedFilter, searchString, sortFragment])

  React.useEffect(() => {
    if (loadInventoryUrl && state.user) {
      setInventoryInfo(null)
      setIsLoadingInventory(true)
      get<PagedResponse<Inventory>>(loadInventoryUrl)
        .then((response) => {
          setIsLoadingInventory(false)
          setPaginationSettings(response.data)
          setInventoryInfo(response.data.results.map((x) => weightValuesToDisplayValues(x)))
        })
        .catch(() => {
          setIsLoadingInventory(false)
          setHasInventoryError(true)
        })
    }
  }, [loadInventoryUrl, state.user]) // we must load user before rendering, to determine if kg or lbs is used for weight

  const onSort = (newSortFragment: string): void => {
    setSortFragment(newSortFragment)
  }

  const isVisibleColumn = (columnName: string): boolean => {
    return hiddenColumns.indexOf(columnName) === -1
  }

  const renderMenuItem = (filter: InventoryFilter, text: string): JSX.Element => {
    return (
      <MenuItem onClick={() => onSelectFilter(filter)}>
        <ListItemIcon>
          {selectedFilter === filter ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
        </ListItemIcon>
        <ListItemText>{text}</ListItemText>
      </MenuItem>
    )
  }

  const renderInventoryRow = (beanInfo: Inventory): JSX.Element => {
    return (
      <TableRow key={beanInfo.url} className="colored-row">
        <TableCell>
          <ResponsiveTableHeader header={t('inventory.name')} />
          {isOsxSafari() ? (
            <MuiLink component={Link} to={`/inventories/${beanInfo.id}`}>
              {beanInfo.name}
            </MuiLink>
          ) : (
            <>
              <Button className="link-cell" component={Link} to={`/inventories/${beanInfo.id}`} />
              {beanInfo.name}
            </>
          )}
        </TableCell>
        {isVisibleColumn(CULTIVAR_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.cultivar')} />
            {beanInfo.cultivar}
          </TableCell>
        )}
        {isVisibleColumn(FARM_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.farm')} />
            {beanInfo.farm}
          </TableCell>
        )}
        {isVisibleColumn(REGION_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.region')} />
            {beanInfo.region}
          </TableCell>
        )}
        {isVisibleColumn(COUNTRY_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.country')} />
            {beanInfo.country}
          </TableCell>
        )}
        {isVisibleColumn(EXPORTER_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.exporter')} />
            {beanInfo.exporter}
          </TableCell>
        )}
        {isVisibleColumn(IMPORTER_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.importer')} />
            {beanInfo.importer}
          </TableCell>
        )}
        {isVisibleColumn(PRODUCER_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.producer')} />
            {beanInfo.producer}
          </TableCell>
        )}
        {isVisibleColumn(ELEVATION_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.elevation')} />
            {beanInfo.elevation ? `${beanInfo.elevation} m` : ''}
          </TableCell>
        )}
        {isVisibleColumn(MOISTURE_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.moisture')} />
            {beanInfo.moisture ? `${beanInfo.moisture}%` : ''}
          </TableCell>
        )}
        {isVisibleColumn(WATER_ACTIVITY_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.wateractivity')} />
            {beanInfo.water_activity || ''}
          </TableCell>
        )}
        {isVisibleColumn(DENSITY_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.density')} />
            {beanInfo.density ? `${beanInfo.density}g/ml` : ''}
          </TableCell>
        )}
        {isVisibleColumn(QUALITY_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.quality')} />
            {!!beanInfo.quality && (
              <Rating
                sx={{ mt: 1, color: 'primary.main', '&.Mui-disabled': { opacity: 1 } }}
                size="small"
                value={beanInfo.quality}
                disabled
              />
            )}
          </TableCell>
        )}
        {isVisibleColumn(PRICE_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.price')} />
            {beanInfo.price ? `${beanInfo.price} $/${getSingleBigWeightSuffix()}` : ''}
          </TableCell>
        )}
        {isVisibleColumn(REG_DATE_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.regdate')} />
            {beanInfo.reg_date ? toDateString(beanInfo.reg_date) : ''}
          </TableCell>
        )}
        {isVisibleColumn(CURRENT_WEIGHT_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.currentweight')} />
            {`${beanInfo.current_weight} ${getBigWeightSuffix()}`}
          </TableCell>
        )}
        {isVisibleColumn(INITIAL_WEIGHT_COLUMN) && (
          <TableCell>
            <ResponsiveTableHeader header={t('inventory.initialweight')} />
            {`${beanInfo.initial_weight} ${getBigWeightSuffix()}`}
          </TableCell>
        )}
      </TableRow>
    )
  }

  return (
    <Container component="main" maxWidth={false}>
      <Popover
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setMenuAnchorEl(null)}
      >
        <Stack sx={{ p: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">{t('logsview.showcolumns')}</FormLabel>
            <FormGroup sx={{ alignItems: 'flex-start' }}>
              {[
                { id: 'STATIC_NAME', label: t('inventory.name'), disabled: true },
                { id: CULTIVAR_COLUMN, label: t('inventory.cultivar'), disabled: false },
                { id: FARM_COLUMN, label: t('inventory.farm'), disabled: false },
                { id: REGION_COLUMN, label: t('inventory.region'), disabled: false },
                { id: COUNTRY_COLUMN, label: t('inventory.country'), disabled: false },
                { id: EXPORTER_COLUMN, label: t('inventory.exporter'), disabled: false },
                { id: IMPORTER_COLUMN, label: t('inventory.importer'), disabled: false },
                { id: PRODUCER_COLUMN, label: t('inventory.producer'), disabled: false },
                { id: ELEVATION_COLUMN, label: t('inventory.elevation'), disabled: false },
                { id: MOISTURE_COLUMN, label: t('inventory.moisture'), disabled: false },
                { id: WATER_ACTIVITY_COLUMN, label: t('inventory.wateractivity'), disabled: false },
                { id: DENSITY_COLUMN, label: t('inventory.density'), disabled: false },
                { id: QUALITY_COLUMN, label: t('inventory.quality'), disabled: false },
                { id: PRICE_COLUMN, label: t('inventory.price'), disabled: false },
                { id: REG_DATE_COLUMN, label: t('inventory.regdate'), disabled: false },
                { id: CURRENT_WEIGHT_COLUMN, label: t('inventory.currentweight'), disabled: false },
                { id: INITIAL_WEIGHT_COLUMN, label: t('inventory.initialweight'), disabled: false },
              ].map((x) => {
                return (
                  <FormControlLabel
                    key={x.id}
                    control={
                      <Checkbox
                        checked={isVisibleColumn(x.id)}
                        disabled={x.disabled}
                        onChange={(_event, isChecked) => {
                          setHiddenColumns((oldHiddenColumns) => {
                            return !isChecked ? [...oldHiddenColumns, x.id] : oldHiddenColumns.filter((y) => y !== x.id)
                          })
                        }}
                      />
                    }
                    label={x.label}
                  />
                )
              })}
            </FormGroup>
          </FormControl>
        </Stack>
      </Popover>
      <Stack
        direction={'column'}
        alignItems={{ xs: 'stretch', md: 'center' }}
        width="100%"
        spacing={2}
        sx={{ position: 'sticky', top: 0, backgroundColor: 'background.default', zIndex: 1 }}
      >
        <ListHeader
          headerText={t('inventory.inventory')}
          searchPlaceholder={t('inventory.searchinventory')}
          typeString={t('inventory.beans')}
          totalResults={paginationSettings?.count || 0}
          onSearch={(newSearchString: string): void => {
            if (newSearchString !== searchString) {
              setSearchString(newSearchString)
            }
          }}
        >
          <TooltipIconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => setFilterMenuAnchorEl(event.currentTarget)}
            tooltip={t('inventory.filterinventory')}
            icon={<FilterAltIcon />}
            isToggled={selectedFilter !== InventoryFilter.All}
          />
          <TooltipIconButton
            tooltip={t('logsview.showhidecolumns')}
            icon={<SettingsIcon />}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(event.currentTarget)}
          />
          {!isScreenBelowMdBreakpoint && (
            <Button variant="contained" component={Link} to={`/inventories/create`}>
              {t('inventory.addinventory')}
            </Button>
          )}
        </ListHeader>
        {isScreenBelowMdBreakpoint && (
          <Stack direction={'row'} sx={{ pb: 1 }}>
            <Button variant="contained" component={Link} to={`/inventories/create`}>
              {t('inventory.addinventory')}
            </Button>
          </Stack>
        )}
      </Stack>
      {inventoryInfo && (
        <>
          <TableContainer>
            <Table className="colored-table collapse-table">
              <TableHead>
                <TableRow>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="name"
                    sortType={SortType.String}
                    onSort={onSort}
                  >
                    {inventoryInfo.length > 0 && inventoryInfo.every((x) => x.is_archived)
                      ? t('inventory.archivedinventory')
                      : t('inventory.name')}
                  </SortableTableHead>

                  {isVisibleColumn(CULTIVAR_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="cultivar"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.cultivar')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(FARM_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="farm"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.farm')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(REGION_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="region"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.region')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(COUNTRY_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="country"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.country')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(EXPORTER_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="exporter"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.exporter')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(IMPORTER_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="importer"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.importer')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(PRODUCER_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="producer"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('inventory.producer')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(ELEVATION_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="elevation"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.elevation')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(MOISTURE_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="moisture"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.moisture')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(WATER_ACTIVITY_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="water_activity"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.wateractivity')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(DENSITY_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="density"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.density')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(QUALITY_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="quality"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.quality')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(PRICE_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="price"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.price')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(REG_DATE_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="reg_date"
                      sortType={SortType.Date}
                      onSort={onSort}
                    >
                      {t('inventory.regdate')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(CURRENT_WEIGHT_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="current_weight"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.currentweight')}
                    </SortableTableHead>
                  )}
                  {isVisibleColumn(INITIAL_WEIGHT_COLUMN) && (
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="initial_weight"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('inventory.initialweight')}
                    </SortableTableHead>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryInfo.filter((row) => !row.is_archived).map((row) => renderInventoryRow(row))}
                {inventoryInfo.filter((row) => !row.is_archived).length > 0 &&
                  inventoryInfo.filter((row) => row.is_archived).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>{t('inventory.archivedinventory')}</TableCell>
                    </TableRow>
                  )}
                {inventoryInfo.filter((row) => row.is_archived).map((beanInfo) => renderInventoryRow(beanInfo))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {isLoadingInventory && <CircularProgress />}
      <Stack sx={{ mt: 1, mb: 4 }}>
        {paginationSettings && (
          <Pagination
            isLoading={isLoadingInventory}
            paginationSettings={paginationSettings}
            onChangePage={(newPageUrl: string) => setLoadInventoryUrl(newPageUrl)}
          />
        )}
      </Stack>
      {hasInventoryError && <Alert severity="error">{t('inventory.loaderror')}</Alert>}
      {inventoryInfo && inventoryInfo.length === 0 && <Alert severity="info">{t('inventory.noinventory')}</Alert>}
      <Menu id="stock-menu" anchorEl={filterMenuAnchorEl} open={Boolean(filterMenuAnchorEl)} onClose={closeFilterMenu}>
        {renderMenuItem(InventoryFilter.All, t('inventory.filterall'))}
        {renderMenuItem(InventoryFilter.Current, t('inventory.filtercurrent'))}
        {renderMenuItem(InventoryFilter.Historic, t('inventory.filterhistoric'))}
      </Menu>
    </Container>
  )
}

export default InventoryView
